import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { LucideIcon } from "lucide-react";

interface Feature {
  text: string;
  description?: string;
  icon: LucideIcon;
  gradient: string;
}

interface FeaturesGridProps {
  title: string;
  subtitle: string;
  badge: {
    icon: LucideIcon;
    text: string;
    color: string; // e.g., "blue"
  };
  features: Feature[];
}

export const FeaturesGrid = ({ title, subtitle, badge, features }: FeaturesGridProps) => {
  const BadgeIcon = badge.icon;

  return (
    <section className="py-12 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center mb-10 md:mb-16">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${badge.color}-100 dark:bg-${badge.color}-900/30 mb-3 md:mb-4`}>
              <BadgeIcon className={`w-3.5 h-3.5 md:w-4 md:h-4 text-${badge.color}-600`} />
              <span className={`text-${badge.color}-700 dark:text-${badge.color}-400 text-xs md:text-sm font-medium`}>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4 px-2">
              {title}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
              {subtitle}
            </p>
          </div>
        </AnimateOnScroll>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className="group bg-card rounded-xl md:rounded-2xl p-4 sm:p-5 md:p-6 shadow-md md:shadow-lg border border-border hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground text-base md:text-lg mb-1.5 md:mb-2">{feature.text}</h3>
                  {feature.description && (
                    <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed flex-grow">{feature.description}</p>
                  )}
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};